import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import type { IconProps } from "@project/ui";
import { useMutation, useQuery, useQueryClient } from "@project/ui";
import { Button, Icon, cn } from "@project/ui";
import { DASHBOARD_PAGE_CONTAINER } from "@apps/dashboard/components/layout/dashboard-page-layout";
import { useI18n } from "@apps/dashboard/contexts/I18nContext";
import { localeToIntlTag } from "@project/i18n";
import { useActiveHouse } from "@apps/dashboard/features/houses/active-house-context";
import { useSyncHouseFromParams } from "@apps/dashboard/features/houses/use-sync-house-from-params";
import {
  copyBudgetMonthFrom,
  createExpenseCategory,
  fetchBudgetCopyPreview,
  fetchBudgetMonthReadiness,
  fetchBudgetMonth,
  fetchDashboardSummary,
  fetchExpenseCategories,
  fetchExpenseSeriesList,
  fetchExpenses,
  homeExpenseQueryKeys,
  replaceBudgetMonth,
} from "@apps/dashboard/lib/home-expense-api";
import { expenseEffectiveMinor } from "@apps/dashboard/lib/expense-effective-amount";
import { formatMinorAsCurrency } from "@apps/dashboard/lib/format-money";
import { minorToMajorInputValue, parseAmountToMinor } from "@apps/dashboard/lib/parse-amount";
import { dashboardPaths } from "@apps/dashboard/lib/routes";
import {
  currentYearMonth,
  isValidYearMonth,
  shiftYearMonth,
  yearMonthToExpenseRange,
} from "@apps/dashboard/lib/year-month";
import { ErrorState } from "@apps/dashboard/components/states/ErrorState";
import { LoadingState } from "@apps/dashboard/components/states/LoadingState";

const PRORATED_CARD_ICONS: IconProps["name"][] = [
  "Home",
  "Shield",
  "Landmark",
  "Car",
  "Receipt",
  "Tag",
];

function expenseSeriesFrequencyLabel(t: (key: string) => string, frequency: string): string {
  const keys: Record<string, string> = {
    monthly: "dashboardUi.expenses.form.frequencyMonthly",
    bimonthly: "dashboardUi.expenses.form.frequencyBimonthly",
    quarterly: "dashboardUi.expenses.form.frequencyQuarterly",
    semiannual: "dashboardUi.expenses.form.frequencySemiannual",
    annual: "dashboardUi.expenses.form.frequencyAnnual",
  };
  return t(keys[frequency] ?? keys.monthly!);
}

export default function BudgetPage(): JSX.Element {
  const { houseId } = useParams<{ houseId: string }>();
  const { t, locale } = useI18n();
  const queryClient = useQueryClient();
  const { houses } = useActiveHouse();
  const [searchParams, setSearchParams] = useSearchParams();
  const actorRole = houseId ? houses?.find((h) => h.id === houseId)?.role : undefined;
  const canEdit = actorRole === "owner" || actorRole === "admin";

  const ymParam = searchParams.get("ym") ?? "";
  const [yearMonth, setYearMonth] = useState(() =>
    isValidYearMonth(ymParam) ? ymParam : currentYearMonth(),
  );

  useEffect(() => {
    if (isValidYearMonth(ymParam) && ymParam !== yearMonth) {
      setYearMonth(ymParam);
    }
  }, [ymParam, yearMonth]);

  useSyncHouseFromParams(houseId);

  const range = useMemo(() => yearMonthToExpenseRange(yearMonth), [yearMonth]);

  const summaryQuery = useQuery({
    queryKey: houseId
      ? homeExpenseQueryKeys.dashboardSummary(locale, houseId, yearMonth)
      : ["dash", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchDashboardSummary(locale, houseId, yearMonth);
    },
    enabled: Boolean(houseId),
  });

  const budgetQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.budgetMonth(locale, houseId, yearMonth) : ["bud", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchBudgetMonth(locale, houseId, yearMonth);
    },
    enabled: Boolean(houseId),
  });

  const categoriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.categories(locale, houseId) : ["cat", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchExpenseCategories(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const expensesQuery = useQuery({
    queryKey: houseId
      ? homeExpenseQueryKeys.expenses(locale, houseId, {
          from: range.from,
          to: range.to,
          contributionScope: "shared",
          sort: "date_desc",
        })
      : ["exp", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchExpenses(locale, houseId, {
        from: range.from,
        to: range.to,
        contributionScope: "shared",
        sort: "date_desc",
      });
    },
    enabled: Boolean(houseId),
  });

  const prevYm = useMemo(() => shiftYearMonth(yearMonth, -1), [yearMonth]);

  const readinessQuery = useQuery({
    queryKey: houseId
      ? ["houses", houseId, "budgets", yearMonth, "readiness", locale]
      : ["budget-ready", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchBudgetMonthReadiness(locale, houseId, yearMonth);
    },
    enabled: Boolean(houseId),
  });

  const copyPreviewQuery = useQuery({
    queryKey: houseId
      ? ["houses", houseId, "budgets", yearMonth, "copy-preview", prevYm, locale]
      : ["budget-copy-preview", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchBudgetCopyPreview(locale, houseId, yearMonth, prevYm);
    },
    enabled: Boolean(houseId),
  });

  const prevSummaryQuery = useQuery({
    queryKey: houseId
      ? homeExpenseQueryKeys.dashboardSummary(locale, houseId, prevYm)
      : ["dash-prev", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchDashboardSummary(locale, houseId, prevYm);
    },
    enabled: Boolean(houseId),
  });

  const seriesQuery = useQuery({
    queryKey: houseId ? homeExpenseQueryKeys.expenseSeriesList(locale, houseId) : ["series-list", "noop"],
    queryFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return fetchExpenseSeriesList(locale, houseId);
    },
    enabled: Boolean(houseId),
  });

  const plannedByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const row of budgetQuery.data ?? []) {
      m.set(row.categoryId, row.plannedAmountMinor);
    }
    return m;
  }, [budgetQuery.data]);

  const spentByCategory = useMemo(() => {
    const m = new Map<string, number>();
    for (const e of expensesQuery.data ?? []) {
      if (!e.paidAt) continue;
      const cid = e.categoryId ?? "";
      if (!cid) continue;
      m.set(cid, (m.get(cid) ?? 0) + expenseEffectiveMinor(e));
    }
    return m;
  }, [expensesQuery.data]);

  const categoryRows = useMemo(() => {
    const cats = [...(categoriesQuery.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    const seen = new Set(cats.map((c) => c.id));
    const extra: { id: string; name: string }[] = [];
    for (const row of budgetQuery.data ?? []) {
      if (!seen.has(row.categoryId)) {
        seen.add(row.categoryId);
        extra.push({
          id: row.categoryId,
          name: row.category?.name ?? row.categoryId,
        });
      }
    }
    return [
      ...cats.map((c) => ({ id: c.id, name: c.name })),
      ...extra,
    ];
  }, [categoriesQuery.data, budgetQuery.data]);

  const proratedSeries = useMemo(() => {
    return (seriesQuery.data ?? [])
      .filter((s) => s.prorated && s.status === "active")
      .slice(0, 6);
  }, [seriesQuery.data]);

  const [plannedInputs, setPlannedInputs] = useState<Record<string, string>>({});
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [addCategoryExpanded, setAddCategoryExpanded] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [newCategoryError, setNewCategoryError] = useState<string | null>(null);

  useEffect(() => {
    const next: Record<string, string> = {};
    for (const row of categoryRows) {
      const minor = plannedByCategory.get(row.id) ?? 0;
      next[row.id] = minor > 0 ? minorToMajorInputValue(minor) : "";
    }
    setPlannedInputs(next);
  }, [categoryRows, plannedByCategory]);

  const totalPlannedMinor = useMemo(() => {
    let s = 0;
    for (const row of categoryRows) {
      const minor = parseAmountToMinor(plannedInputs[row.id] ?? "");
      if (minor != null && minor > 0) s += minor;
    }
    return s;
  }, [categoryRows, plannedInputs]);

  const currency = summaryQuery.data?.currency ?? "EUR";
  const spentMonthMinor = summaryQuery.data?.spentMinor ?? 0;
  const capMinor = summaryQuery.data?.monthlyBudgetCapMinor;
  const targetMinorGoal =
    totalPlannedMinor > 0
      ? totalPlannedMinor
      : capMinor != null && capMinor > 0
        ? capMinor
        : 0;
  const goalRawPct =
    targetMinorGoal > 0 ? (spentMonthMinor / targetMinorGoal) * 100 : 0;
  const progressPct = Math.min(100, Math.round(goalRawPct));
  const progressLabelPct = Math.round(goalRawPct);
  const openingBalanceMinor = summaryQuery.data?.openingBankBalanceMinor ?? 0;
  const retainedMinor = summaryQuery.data?.proratedReserveMinor ?? 0;
  const projectedBankBalanceMinor =
    openingBalanceMinor + targetMinorGoal - spentMonthMinor - retainedMinor;

  const prevPlannedMinor = prevSummaryQuery.data?.plannedBudgetMinor ?? 0;
  const plannedDeltaPct =
    prevPlannedMinor > 0 ? ((totalPlannedMinor - prevPlannedMinor) / prevPlannedMinor) * 100 : null;

  const formatDeltaPct = (n: number) =>
    new Intl.NumberFormat(localeToIntlTag(locale), {
      maximumFractionDigits: 1,
      minimumFractionDigits: 0,
    }).format(n);

  const analysisCategoryRows = useMemo(() => {
    return categoryRows.filter((row) => {
      const fromInput = parseAmountToMinor(plannedInputs[row.id] ?? "");
      const plannedMinor =
        canEdit && fromInput != null && fromInput > 0
          ? fromInput
          : (plannedByCategory.get(row.id) ?? 0);
      const spentMinor = spentByCategory.get(row.id) ?? 0;
      return plannedMinor > 0 || spentMinor > 0;
    });
  }, [categoryRows, plannedByCategory, spentByCategory, plannedInputs, canEdit]);

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!houseId) throw new Error("missing houseId");
      const allocations: { categoryId: string; plannedAmountMinor: number }[] = [];
      for (const row of categoryRows) {
        const minor = parseAmountToMinor(plannedInputs[row.id] ?? "");
        if (minor != null && minor > 0) {
          allocations.push({ categoryId: row.id, plannedAmountMinor: minor });
        }
      }
      return replaceBudgetMonth(locale, houseId, yearMonth, { allocations });
    },
    onSuccess: async () => {
      setSaveMsg(t("dashboardUi.budget.saveSuccess"));
      if (houseId) {
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.budgetMonth(locale, houseId, yearMonth),
        });
        await queryClient.invalidateQueries({
          queryKey: ["houses", houseId, "dashboard", "summary"],
        });
      }
    },
  });

  const createCategoryMutation = useMutation({
    mutationFn: (name: string) => {
      if (!houseId) throw new Error("missing houseId");
      const maxSort = (categoriesQuery.data ?? []).reduce((acc, c) => Math.max(acc, c.sortOrder), -1);
      return createExpenseCategory(locale, houseId, {
        name,
        sortOrder: maxSort + 1,
      });
    },
    onSuccess: async () => {
      setAddCategoryExpanded(false);
      setNewCategoryName("");
      setNewCategoryError(null);
      if (houseId) {
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.categories(locale, houseId),
        });
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.budgetMonth(locale, houseId, yearMonth),
        });
        await queryClient.invalidateQueries({
          queryKey: ["houses", houseId, "expenses"],
        });
      }
    },
  });

  const copyBudgetMutation = useMutation({
    mutationFn: () => {
      if (!houseId) throw new Error("missing houseId");
      return copyBudgetMonthFrom(locale, houseId, yearMonth, {
        sourceYearMonth: prevYm,
        replaceExisting: true,
      });
    },
    onSuccess: async () => {
      setSaveMsg(t("dashboardUi.budget.saveSuccess"));
      if (houseId) {
        await queryClient.invalidateQueries({
          queryKey: homeExpenseQueryKeys.budgetMonth(locale, houseId, yearMonth),
        });
        await queryClient.invalidateQueries({
          queryKey: ["houses", houseId, "dashboard", "summary"],
        });
        await queryClient.invalidateQueries({
          queryKey: ["houses", houseId, "budgets", yearMonth, "readiness", locale],
        });
      }
    },
  });

  function setMonth(ym: string): void {
    setYearMonth(ym);
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev);
      p.set("ym", ym);
      return p;
    });
  }

  if (!houseId) {
    return (
      <div className={DASHBOARD_PAGE_CONTAINER}>
        <ErrorState />
      </div>
    );
  }

  const loading =
    summaryQuery.isLoading ||
    budgetQuery.isLoading ||
    categoriesQuery.isLoading ||
    expensesQuery.isLoading ||
    seriesQuery.isLoading;

  return (
    <div className={cn(DASHBOARD_PAGE_CONTAINER, "space-y-10")}>
      <div
        className="flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between"
        data-tour="budget-hero-section"
      >
        <div className="max-w-2xl flex-1 space-y-4">
          <div className="flex flex-wrap items-center gap-3 gap-y-2">
            <span className="text-label-md font-bold tracking-widest text-on-surface-variant">
              {t("dashboardUi.budget.heroEyebrow")}
            </span>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl"
                aria-label={t("dashboardUi.budget.prevMonth")}
                onClick={() => setMonth(shiftYearMonth(yearMonth, -1))}
              >
                <Icon name="ChevronLeft" className="size-4" strokeWidth={1.5} />
              </Button>
              <label className="flex items-center gap-2 text-label-md text-on-surface-variant">
                <span className="sr-only">{t("dashboardUi.budget.monthNav")}</span>
                <input
                  type="month"
                  value={yearMonth}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (isValidYearMonth(v)) setMonth(v);
                  }}
                  className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-3 py-2 font-body text-sm text-on-surface"
                  aria-label={t("dashboardUi.budget.monthNav")}
                />
              </label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="rounded-xl"
                aria-label={t("dashboardUi.budget.nextMonth")}
                onClick={() => setMonth(shiftYearMonth(yearMonth, 1))}
              >
                <Icon name="ChevronRight" className="size-4" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
          <h1 className="font-headline text-4xl font-extrabold tracking-tighter text-primary md:text-5xl lg:text-6xl">
            {t("dashboardUi.budget.heroTitle")}
          </h1>
          <p className="max-w-xl font-body text-body-md leading-relaxed text-on-surface-variant md:text-lg">
            {t("dashboardUi.budget.heroSubtitle")}
          </p>
          {houseId ? (
            <p className="pt-2">
              <Link
                to={dashboardPaths.houseAnalytics(houseId)}
                className="inline-flex items-center gap-2 font-label text-sm font-semibold text-primary underline-offset-4 hover:underline"
              >
                <Icon name="LineChart" className="size-4" strokeWidth={1.5} aria-hidden />
                {t("dashboardUi.analytics.openFromBudget")}
              </Link>
            </p>
          ) : null}
        </div>
        <div className="shrink-0 text-left lg:text-right">
          <span className="block font-headline text-4xl font-black tabular-nums text-primary md:text-5xl">
            {formatMinorAsCurrency(totalPlannedMinor, currency, locale)}
          </span>
          {plannedDeltaPct != null && Math.abs(plannedDeltaPct) >= 0.05 ? (
            <span
              className={cn(
                "mt-1 block text-sm font-bold",
                plannedDeltaPct > 0 ? "text-secondary" : "text-tertiary",
              )}
            >
              {plannedDeltaPct > 0
                ? t("dashboardUi.budget.heroVsPrevUp", {
                    pct: formatDeltaPct(plannedDeltaPct),
                  })
                : t("dashboardUi.budget.heroVsPrevDown", {
                    pct: formatDeltaPct(Math.abs(plannedDeltaPct)),
                  })}
            </span>
          ) : prevPlannedMinor > 0 ? (
            <span className="mt-1 block text-sm font-bold text-on-surface-variant">
              {t("dashboardUi.budget.heroVsPrevFlat")}
            </span>
          ) : (
            <span className="mt-1 block text-label-md text-on-surface-variant">
              {yearMonth}
            </span>
          )}
        </div>
      </div>

      {loading ? (
        <div
          className="rounded-2xl bg-surface-container-low/80 p-8 outline outline-1 outline-outline-variant/15"
          data-tour="budget-goal-section"
        >
          <LoadingState />
        </div>
      ) : summaryQuery.isError || budgetQuery.isError ? (
        <div data-tour="budget-goal-section">
          <ErrorState
            onRetry={() => {
              void summaryQuery.refetch();
              void budgetQuery.refetch();
            }}
          />
        </div>
      ) : (
        <>
          <section className="rounded-2xl bg-surface-container-low p-6 md:p-8" data-tour="budget-goal-section">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
              <span className="text-label-md font-bold tracking-[0.2em] text-primary">
                {t("dashboardUi.budget.goalLabel", { yearMonth })}
              </span>
              <span className="font-headline text-lg font-bold text-primary">
                {t("dashboardUi.budget.percentComplete", { percent: progressLabelPct })}
              </span>
            </div>
            <div className="perspective-progress h-8 w-full overflow-hidden rounded-full bg-surface-container-highest">
              <div
                className="perspective-progress-fill relative h-full rounded-full bg-gradient-to-r from-primary to-primary-container"
                style={{ width: `${progressPct}%` }}
              >
                <div className="absolute inset-0 bg-white/10 blur-[1px]" />
              </div>
            </div>
            <div className="mt-4 flex justify-between text-label-md font-semibold tracking-wider text-on-surface-variant">
              <span>
                {t("dashboardUi.budget.reservedLabel", {
                  amount: formatMinorAsCurrency(spentMonthMinor, currency, locale),
                })}
              </span>
              <span>
                {t("dashboardUi.budget.targetLabel", {
                  amount: formatMinorAsCurrency(targetMinorGoal, currency, locale),
                })}
              </span>
            </div>
            {capMinor != null && capMinor > 0 ? (
              <p className="mt-3 text-body-sm text-on-surface-variant">
                {t("dashboardUi.budget.monthlyCapHint")}: {formatMinorAsCurrency(capMinor, currency, locale)}
              </p>
            ) : null}
          </section>

          <section className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-6">
            <h2 className="font-headline text-lg font-bold text-primary">
              {t("dashboardUi.budget.moneySnapshotTitle")}
            </h2>
            <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
              <article className="rounded-xl bg-surface-container-low p-4">
                <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
                  {t("dashboardUi.budget.moneySnapshotOpening")}
                </p>
                <p className="mt-1 font-headline text-xl font-bold text-primary tabular-nums">
                  {formatMinorAsCurrency(openingBalanceMinor, currency, locale)}
                </p>
              </article>
              <article className="rounded-xl bg-surface-container-low p-4">
                <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
                  {t("dashboardUi.budget.moneySnapshotNeedMonth")}
                </p>
                <p className="mt-1 font-headline text-xl font-bold text-primary tabular-nums">
                  {formatMinorAsCurrency(targetMinorGoal, currency, locale)}
                </p>
              </article>
              <article className="rounded-xl bg-surface-container-low p-4">
                <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
                  {t("dashboardUi.budget.moneySnapshotRetained")}
                </p>
                <p className="mt-1 font-headline text-xl font-bold text-secondary tabular-nums">
                  {formatMinorAsCurrency(retainedMinor, currency, locale)}
                </p>
              </article>
              <article className="rounded-xl bg-surface-container-low p-4">
                <p className="text-label-sm uppercase tracking-wide text-on-surface-variant">
                  {t("dashboardUi.budget.moneySnapshotProjectedBalance")}
                </p>
                <p className="mt-1 font-headline text-xl font-bold text-tertiary tabular-nums">
                  {formatMinorAsCurrency(projectedBankBalanceMinor, currency, locale)}
                </p>
              </article>
            </div>
          </section>

          <section className="space-y-6">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <h2 className="font-headline text-title-md font-bold text-primary md:text-xl">
                {t("dashboardUi.budget.proratedTitle")}
              </h2>
              <Link
                to={dashboardPaths.houseExpenses(houseId)}
                className="flex items-center gap-1 text-sm font-bold text-primary hover:underline"
              >
                {t("dashboardUi.budget.proratedLinkJournal")}
                <Icon name="ArrowRight" className="size-4" strokeWidth={2} aria-hidden />
              </Link>
            </div>
            {proratedSeries.length === 0 ? (
              <p className="rounded-2xl border border-outline-variant/15 bg-surface-container-low px-6 py-8 text-body-md text-on-surface-variant">
                {t("dashboardUi.budget.proratedEmpty")}
              </p>
            ) : (
              <ul className="grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                {proratedSeries.map((s, idx) => {
                  const icon = PRORATED_CARD_ICONS[idx % PRORATED_CARD_ICONS.length]!;
                  const tone =
                    idx % 3 === 0
                      ? "bg-secondary/10 text-secondary"
                      : idx % 3 === 1
                        ? "bg-primary/10 text-primary"
                        : "bg-tertiary/10 text-tertiary";
                  const totalMinor = s.totalAmountMinor;
                  return (
                    <li key={s.id}>
                      <Link
                        to={dashboardPaths.houseExpenseSeries(houseId, s.id)}
                        className="block h-full rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm transition-shadow hover:shadow-lg"
                      >
                        <div className="mb-8 flex items-start justify-between gap-3">
                          <div
                            className={cn("flex size-12 items-center justify-center rounded-xl", tone)}
                            aria-hidden
                          >
                            <Icon name={icon} className="size-6" strokeWidth={1.5} />
                          </div>
                          <span className="text-label-md font-bold uppercase text-on-surface-variant">
                            {expenseSeriesFrequencyLabel(t, s.frequency)}
                          </span>
                        </div>
                        <h3 className="font-headline text-xl font-bold text-primary">{s.title}</h3>
                        <p className="mt-1 text-xs text-on-surface-variant">{t("dashboardUi.budget.proratedReserveHint")}</p>
                        <div className="mt-6 flex items-end justify-between gap-3">
                          <span className="font-headline text-2xl font-black tabular-nums text-primary">
                            {formatMinorAsCurrency(s.installmentAmountMinor, s.currency, locale)}
                          </span>
                          {totalMinor != null && totalMinor > 0 ? (
                            <span
                              className={cn(
                                "rounded-lg px-2 py-1 text-xs font-bold",
                                idx % 3 === 0
                                  ? "bg-secondary/15 text-secondary"
                                  : idx % 3 === 1
                                    ? "bg-primary-fixed/40 text-primary"
                                    : "bg-tertiary-fixed/40 text-tertiary",
                              )}
                            >
                              {t("dashboardUi.budget.proratedTotalBadge", {
                                amount: formatMinorAsCurrency(totalMinor, s.currency, locale),
                              })}
                            </span>
                          ) : null}
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          <section className="rounded-3xl border border-outline-variant/15 bg-surface-container-lowest p-8 md:p-10">
            <div className="mb-10 flex flex-col justify-between gap-8 md:flex-row md:items-start">
              <div className="max-w-xl space-y-2">
                <h2 className="font-headline text-2xl font-bold tracking-tight text-primary md:text-3xl">
                  {t("dashboardUi.budget.performanceTitle")}
                </h2>
                <p className="text-body-md text-on-surface-variant">{t("dashboardUi.budget.performanceSubtitle")}</p>
              </div>
              <div className="flex flex-wrap gap-6 md:gap-8">
                <span className="flex items-center gap-3 text-label-md font-bold uppercase tracking-widest text-on-surface-variant">
                  <span className="size-3 shrink-0 rounded-full bg-primary" aria-hidden />
                  {t("dashboardUi.budget.legendBudgeted")}
                </span>
                <span className="flex items-center gap-3 text-label-md font-bold uppercase tracking-widest text-on-surface-variant">
                  <span className="size-3 shrink-0 rounded-full bg-secondary" aria-hidden />
                  {t("dashboardUi.budget.legendSpent")}
                </span>
              </div>
            </div>
            {analysisCategoryRows.length === 0 ? (
              <p className="rounded-xl bg-surface-container-low px-6 py-10 text-center text-body-md text-on-surface-variant">
                {t("dashboardUi.budget.analysisEmpty")}
              </p>
            ) : (
              <ul className="space-y-10">
                {analysisCategoryRows.map((row) => {
                  const fromInput = parseAmountToMinor(plannedInputs[row.id] ?? "");
                  const plannedMinor =
                    canEdit && fromInput != null && fromInput > 0
                      ? fromInput
                      : (plannedByCategory.get(row.id) ?? 0);
                  const spentMinor = spentByCategory.get(row.id) ?? 0;
                  const maxRef = Math.max(plannedMinor, spentMinor, 1);
                  const wPlanned = (plannedMinor / maxRef) * 100;
                  const wSpent = (spentMinor / maxRef) * 100;
                  let badgeKey: "over" | "under" | "on" | "none" = "none";
                  if (plannedMinor <= 0 && spentMinor > 0) badgeKey = "none";
                  else if (plannedMinor > 0 && spentMinor > plannedMinor) badgeKey = "over";
                  else if (plannedMinor > 0 && spentMinor < plannedMinor) badgeKey = "under";
                  else if (plannedMinor > 0) badgeKey = "on";
                  const badgeText =
                    badgeKey === "over"
                      ? t("dashboardUi.budget.badgeOverBudget")
                      : badgeKey === "under"
                        ? t("dashboardUi.budget.badgeUnderBudget")
                        : badgeKey === "on"
                          ? t("dashboardUi.budget.badgeOnTrack")
                          : t("dashboardUi.budget.badgeNoPlan");
                  const badgeClass =
                    badgeKey === "over"
                      ? "bg-error-container/40 text-error"
                      : badgeKey === "under"
                        ? "bg-surface-container text-on-surface-variant"
                        : badgeKey === "on"
                          ? "bg-secondary/15 text-secondary"
                          : "bg-surface-container text-on-surface-variant";
                  const realClass = badgeKey === "over" ? "text-error" : "text-primary";
                  return (
                    <li key={row.id} className="flex flex-col gap-4">
                      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="min-w-[7.5rem] font-headline text-lg font-bold text-primary">
                            {row.name}
                          </span>
                          <span
                            className={cn(
                              "rounded-md px-2 py-1 text-xs font-semibold",
                              badgeClass,
                            )}
                          >
                            {badgeText}
                          </span>
                        </div>
                        <div className="flex gap-8 sm:gap-10">
                          <div className="text-right">
                            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                              {t("dashboardUi.budget.columnBudget")}
                            </p>
                            <p className="text-sm font-bold tabular-nums text-on-surface">
                              {formatMinorAsCurrency(plannedMinor, currency, locale)}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                              {t("dashboardUi.budget.columnReal")}
                            </p>
                            <p className={cn("text-sm font-black tabular-nums", realClass)}>
                              {formatMinorAsCurrency(spentMinor, currency, locale)}
                            </p>
                          </div>
                        </div>
                      </div>
                      <div className="relative h-2 w-full overflow-hidden rounded-full bg-surface-container-highest">
                        <div
                          className="absolute inset-y-0 left-0 rounded-full bg-primary/25"
                          style={{ width: `${wPlanned}%` }}
                          aria-hidden
                        />
                        <div
                          className="absolute inset-y-0 left-0 rounded-full bg-secondary"
                          style={{ width: `${wSpent}%` }}
                          aria-hidden
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>

          {!readinessQuery.data?.ready ? (
            <section className="rounded-2xl border border-warning-500/30 bg-warning-50 p-4 dark:bg-warning-950/20">
              <p className="text-label-sm font-semibold text-warning-700 dark:text-warning-300">
                {t("dashboardUi.notificationsInApp.budgetMonthNeedsSetupTitle", {
                  houseName: "",
                })}
              </p>
              <p className="mt-1 text-body-sm text-warning-700/90 dark:text-warning-300/90">
                {t("dashboardUi.notificationsInApp.budgetMonthNeedsSetupBody", {
                  houseName: "",
                  yearMonth,
                })}
              </p>
              <div className="mt-4 rounded-xl border border-outline-variant/20 bg-surface-container-low p-3">
                <p className="text-label-sm font-semibold text-on-surface">
                  {t("dashboardUi.budget.planningSectionTitle")}
                </p>
                <p className="mt-1 text-body-sm text-on-surface-variant">
                  {copyPreviewQuery.data
                    ? `${copyPreviewQuery.data.linesCount} · ${formatMinorAsCurrency(copyPreviewQuery.data.totalMinor, currency, locale)}`
                    : ""}
                </p>
                {copyPreviewQuery.data?.lines?.length ? (
                  <div className="mt-3 max-h-40 space-y-1 overflow-auto text-sm text-on-surface-variant">
                    {copyPreviewQuery.data.lines.map((line) => (
                      <div key={line.categoryId} className="flex items-center justify-between">
                        <span>{line.categoryName}</span>
                        <span>{formatMinorAsCurrency(line.plannedAmountMinor, currency, locale)}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                <div className="mt-4">
                  <Button
                    type="button"
                    disabled={!canEdit || copyBudgetMutation.isPending}
                    onClick={() => {
                      const confirmed = window.confirm(
                        `${t("dashboardUi.budget.copyFromPreviousConfirmTitle")}\n\n${t("dashboardUi.budget.copyFromPreviousConfirmMessage")}`,
                      );
                      if (confirmed) copyBudgetMutation.mutate();
                    }}
                  >
                    {copyBudgetMutation.isPending
                      ? t("dashboardUi.budget.saving")
                      : t("dashboardUi.budget.copyFromPreviousCta")}
                  </Button>
                </div>
              </div>
            </section>
          ) : null}

          <section className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-headline text-title-md font-bold text-primary md:text-xl">
                {t("dashboardUi.budget.planningSectionTitle")}
              </h2>
              {canEdit ? (
                <div className="flex flex-wrap items-center gap-3">
                  {saveMsg ? <span className="text-body-md text-secondary">{saveMsg}</span> : null}
                  <Button
                    type="button"
                    className="primary-gradient rounded-xl text-on-primary"
                    disabled={saveMutation.isPending}
                    onClick={() => {
                      setSaveMsg(null);
                      void saveMutation.mutateAsync().catch(() => setSaveMsg(null));
                    }}
                  >
                    {saveMutation.isPending ? t("dashboardUi.budget.saving") : t("dashboardUi.budget.saveCta")}
                  </Button>
                </div>
              ) : (
                <p className="text-body-md text-on-surface-variant">{t("dashboardUi.budget.readOnlyHint")}</p>
              )}
            </div>

            {categoryRows.length === 0 && !canEdit ? (
              <p className="rounded-2xl bg-surface-container-low px-6 py-8 text-center text-body-md text-on-surface-variant outline outline-1 outline-outline-variant/15">
                {t("dashboardUi.budget.noCategoriesHint")}
              </p>
            ) : (
              <>
                {categoryRows.length === 0 && canEdit ? (
                  <p className="mb-4 text-body-md text-on-surface-variant">{t("dashboardUi.budget.noCategoriesHint")}</p>
                ) : null}
                <ul className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                  {categoryRows.map((row, idx) => {
                    const plannedFromInput = parseAmountToMinor(plannedInputs[row.id] ?? "");
                    const plannedMinor = canEdit
                      ? (plannedFromInput ?? plannedByCategory.get(row.id) ?? 0)
                      : (plannedByCategory.get(row.id) ?? 0);
                    const spentMinor = spentByCategory.get(row.id) ?? 0;
                    const pct =
                      plannedMinor > 0 ? Math.min(100, Math.round((spentMinor / plannedMinor) * 100)) : 0;
                    const tone =
                      idx % 3 === 0
                        ? "bg-secondary/10 text-secondary"
                        : idx % 3 === 1
                          ? "bg-primary/10 text-primary"
                          : "bg-tertiary/10 text-tertiary";
                    return (
                      <li
                        key={row.id}
                        className="rounded-2xl border border-outline-variant/15 bg-surface-container-lowest p-6 shadow-sm transition-shadow hover:shadow-md"
                      >
                        <div className="mb-6 flex items-start justify-between gap-3">
                          <div
                            className={`flex size-12 items-center justify-center rounded-xl ${tone}`}
                            aria-hidden
                          >
                            <Icon name="Tag" className="size-6" strokeWidth={1.5} />
                          </div>
                        </div>
                        <h3 className="font-headline text-lg font-bold text-primary">{row.name}</h3>
                        <div className="mt-4 space-y-2">
                          <div className="flex justify-between text-label-md uppercase tracking-wider text-on-surface-variant">
                            <span>{t("dashboardUi.budget.spentLabel")}</span>
                            <span className="font-headline font-semibold text-primary tabular-nums normal-case">
                              {formatMinorAsCurrency(spentMinor, currency, locale)}
                            </span>
                          </div>
                          {canEdit ? (
                            <label className="block">
                              <span className="text-label-md uppercase tracking-wider text-on-surface-variant">
                                {t("dashboardUi.budget.plannedLabel")}
                              </span>
                              <input
                                type="text"
                                inputMode="decimal"
                                value={plannedInputs[row.id] ?? ""}
                                onChange={(e) =>
                                  setPlannedInputs((prev) => ({ ...prev, [row.id]: e.target.value }))
                                }
                                placeholder="0,00"
                                className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-high px-3 py-2 font-body text-on-surface"
                              />
                              <span className="mt-1 block text-[10px] uppercase tracking-wider text-on-surface-variant">
                                {t("dashboardUi.budget.plannedInputHint")}
                              </span>
                            </label>
                          ) : (
                            <p className="font-headline text-xl font-black text-primary tabular-nums">
                              {formatMinorAsCurrency(plannedMinor, currency, locale)}
                            </p>
                          )}
                          <div className="perspective-progress mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-container-highest">
                            <div
                              className="h-full rounded-full bg-gradient-to-r from-secondary to-secondary-container"
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                        </div>
                      </li>
                    );
                  })}
                  {canEdit ? (
                    <li className="flex min-h-[280px] flex-col rounded-2xl border-2 border-dashed border-outline-variant/40 bg-surface-container-low/50 p-6 transition-colors hover:border-primary/30 hover:bg-surface-container-low">
                      {!addCategoryExpanded ? (
                        <button
                          type="button"
                          className="flex flex-1 flex-col items-center justify-center gap-3 rounded-xl py-8 text-center outline-none focus-visible:ring-2 focus-visible:ring-primary"
                          onClick={() => {
                            setAddCategoryExpanded(true);
                            setNewCategoryError(null);
                          }}
                        >
                          <div className="flex size-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <Icon name="Plus" className="size-8" strokeWidth={1.5} />
                          </div>
                          <span className="font-headline text-lg font-bold text-primary">
                            {t("dashboardUi.budget.addCategoryCard")}
                          </span>
                          <span className="max-w-[14rem] text-body-md text-on-surface-variant">
                            {t("dashboardUi.budget.addCategoryCardHint")}
                          </span>
                        </button>
                      ) : (
                        <form
                          className="flex flex-1 flex-col gap-4"
                          onSubmit={(e) => {
                            e.preventDefault();
                            setNewCategoryError(null);
                            const trimmed = newCategoryName.trim();
                            if (!trimmed) {
                              setNewCategoryError(t("validation.required"));
                              return;
                            }
                            void createCategoryMutation.mutateAsync(trimmed).catch((err: unknown) => {
                              setNewCategoryError(
                                err instanceof Error ? err.message : t("dashboardUi.states.errorHint"),
                              );
                            });
                          }}
                        >
                          <p className="font-headline text-base font-bold text-primary">
                            {t("dashboardUi.budget.addCategoryFormTitle")}
                          </p>
                          <label className="block">
                            <span className="text-label-md uppercase tracking-wider text-on-surface-variant">
                              {t("dashboardUi.budget.addCategoryNameLabel")}
                            </span>
                            <input
                              type="text"
                              value={newCategoryName}
                              onChange={(e) => setNewCategoryName(e.target.value)}
                              placeholder={t("dashboardUi.budget.addCategoryNamePlaceholder")}
                              autoComplete="off"
                              className="mt-1 w-full rounded-xl border border-outline-variant/30 bg-surface-container-high px-3 py-2 font-body text-on-surface"
                            />
                          </label>
                          {newCategoryError ? (
                            <p className="text-body-md text-error">{newCategoryError}</p>
                          ) : null}
                          <div className="mt-auto flex flex-wrap gap-2">
                            <Button
                              type="submit"
                              className="primary-gradient rounded-xl text-on-primary"
                              disabled={createCategoryMutation.isPending}
                            >
                              {createCategoryMutation.isPending
                                ? t("dashboardUi.budget.addCategoryCreating")
                                : t("dashboardUi.budget.addCategorySubmit")}
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              className="rounded-xl"
                              disabled={createCategoryMutation.isPending}
                              onClick={() => {
                                setAddCategoryExpanded(false);
                                setNewCategoryName("");
                                setNewCategoryError(null);
                              }}
                            >
                              {t("dashboardUi.budget.addCategoryCancel")}
                            </Button>
                          </div>
                        </form>
                      )}
                    </li>
                  ) : null}
                </ul>
              </>
            )}
          </section>

          <section
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary via-primary-container to-primary text-on-primary shadow-lg"
            aria-labelledby="budget-banner-title"
          >
            <div
              className="pointer-events-none absolute -right-20 -top-20 size-72 rounded-full bg-white/10 blur-3xl"
              aria-hidden
            />
            <div className="relative px-8 py-12 md:px-12 md:py-14">
              <p className="text-label-md font-bold uppercase tracking-[0.25em] text-on-primary/80">
                {t("dashboardUi.budget.bannerEyebrow")}
              </p>
              <h2 id="budget-banner-title" className="mt-3 max-w-lg font-headline text-2xl font-bold tracking-tight md:text-3xl">
                {t("dashboardUi.budget.bannerTitle")}
              </h2>
              <Button
                className="mt-8 border border-white/30 bg-white/15 text-on-primary hover:bg-white/25"
                variant="outline"
                asChild
              >
                <Link to={dashboardPaths.houseExpenses(houseId)}>{t("dashboardUi.budget.proratedLinkJournal")}</Link>
              </Button>
            </div>
          </section>

          <p className="text-center text-label-md text-outline">
            <Link to={dashboardPaths.houseMembers(houseId)} className="text-primary underline-offset-2 hover:underline">
              {t("dashboardUi.members.splitOpenBudgetLink")}
            </Link>
          </p>
        </>
      )}
    </div>
  );
}
